import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Sidebar from "@/pages/sidebar/sidebar";
import { useInvoice } from "@/hooks/invoice/use-invoice";
import { EditInvoiceForm } from "@/components/invoices/create-invoice-form/edit-invoice-form";
import { CreateInvoiceProvider } from "@/components/invoices/create-invoice-form/create-invoice-context";
import { PreviewButton } from "@/components/invoices/create-invoice-form/preview-button";
import { LimitedAccountWarning } from "@/components/limited-account-warning";
import { MerchantProcessingBanner } from "@/components/merchant-processing-banner";
import { Button } from "@/components/ui/button-tw";
import { ArrowLeftIcon } from "lucide-react";
import Loading from "@/components/ui/loading";
import { CreateInvoiceFormData } from "@/components/invoices/create-invoice-form/schema";

const EditInvoice = () => {
  const history = useHistory();
  const { id } = useParams();
  const [currentWidth, setCurrentWidth] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentFormData, setCurrentFormData] =
    useState<CreateInvoiceFormData | null>(null);

  //@ts-ignore
  const { data: invoice, isLoading, error } = useInvoice({ invoiceId: id });

  if (isLoading) {
    return (
      <div className="h-100">
        <Sidebar
          currentWidth={currentWidth}
          setCurrentWidth={setCurrentWidth}
        />
        <Loading message="Loading invoice..." />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="h-100">
        <Sidebar
          currentWidth={currentWidth}
          setCurrentWidth={setCurrentWidth}
        />
        <div className="relative lg:ml-80 h-full bg-gray-50 px-3 pt-6 pb-12">
          <div className="max-w-[1720px] mx-auto">
            <div className="text-center py-12">
              <h2 className="text-2xl font-medium text-gray-900 mb-4">
                Invoice not found
              </h2>
              <p className="text-gray-600 mb-6">
                The invoice you're looking for could not be found.
              </p>
              <Button onClick={() => history.push("/invoices")}>
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back to Invoices
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CreateInvoiceProvider
      formData={currentFormData}
      isPreviewOpen={isPreviewOpen}
      setIsPreviewOpen={setIsPreviewOpen}
    >
      <div className="min-h-full">
        <Sidebar
          currentWidth={currentWidth}
          setCurrentWidth={setCurrentWidth}
        />

        <LimitedAccountWarning />
        <MerchantProcessingBanner />
        <div className="relative lg:ml-80 h-full bg-gray-50 px-0 md:px-3 pt-4 md:pt-6 xl:pt-8 pb-12 xl:px-6 2xl:px-8 lg:pb-32">
          <div className="max-w-[1720px] mx-auto">
            <div className="flex justify-between items-center mb-4 md:mb-6 xl:mb-8 px-4 md:px-0">
              <div className="flex gap-x-4 items-center">
                <Button
                  variant="ghost"
                  onClick={() => history.push("/invoices")}
                  className="p-2 hover:bg-gray-100"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </Button>
                <span className="text-xl md:text-3xl font-medium text-gray-900">
                  Edit Invoice {invoice?.invoice_info?.invoice_number || ``}
                </span>
              </div>
              <div className="flex gap-x-4">
                <PreviewButton />
              </div>
            </div>
            <div className="mx-auto grid grid-cols-5 gap-0 md:gap-6 items-start">
              <EditInvoiceForm
                invoiceId={id}
                initialData={invoice}
                onFormDataChange={setCurrentFormData}
              />
            </div>
          </div>
        </div>
      </div>
    </CreateInvoiceProvider>
  );
};

export default EditInvoice;
